import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, of, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  ApplicationDialogService,
  MessageBoxComponent,
  PortalHotToastService,
  setupUntilDestroy,
} from '@portal-workspace/grow-ui-library';
import {
  ConfirmationDialogResult,
  DYNABOT_KNOWLEDGE_FILE_MAX_LENGTH,
  DYNABOT_KNOWLEDGE_FILE_NAME_MAX_LENGTH,
  DYNABOT_KNOWLEDGE_FILE_NAME_PATTERN,
  DynabotKnowledgeFile,
} from '@portal-workspace/grow-shared-library';
import { DynabotService } from '../../service/dynabot.service';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './dynabot-knowledge.page.html',
  styleUrls: ['./dynabot-knowledge.page.scss'],
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MessageBoxComponent,
  ],
})
export class DynabotKnowledgePage implements OnInit {

  readonly maxContentLength = DYNABOT_KNOWLEDGE_FILE_MAX_LENGTH;

  subscriptions: Subscription[] = [];

  files: DynabotKnowledgeFile[] = [];
  /** null while writing a new file. */
  selectedFileName: string | null = null;

  formControlFileName = new FormControl<string>('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.maxLength(DYNABOT_KNOWLEDGE_FILE_NAME_MAX_LENGTH),
      Validators.pattern(DYNABOT_KNOWLEDGE_FILE_NAME_PATTERN),
      (control: AbstractControl): ValidationErrors | null => this.fileNameTakenValidator(control),
    ],
  });
  formControlContent = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.maxLength(DYNABOT_KNOWLEDGE_FILE_MAX_LENGTH)],
  });
  formGroup = new FormGroup({
    fileName: this.formControlFileName,
    content: this.formControlContent,
  });

  constructor(
    private dynabotService: DynabotService,
    private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
  ) {}

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.load();
  }

  get isNewFile(): boolean {
    return this.selectedFileName === null;
  }

  get selectedFile(): DynabotKnowledgeFile | null {
    return this.files.find(f => f.fileName === this.selectedFileName) ?? null;
  }

  load(): void {
    this.subscriptions.push(this.dynabotService.getDynabotKnowledgeFilesFn().pipe(
      this.toastService.topMenuLoadingObservable(),
      this.toastService.publishErrorNotificationObservable({
        errorTitle: 'Unable to load DynaBot context files',
        errorMessage: 'Please try again.',
        retryFn: () => this.load(),
      }),
    ).subscribe(response => {
      this.files = response.payload;
      this.selectFirstFile();
    }));
  }

  onSelectFile(file: DynabotKnowledgeFile): void {
    if (file.fileName === this.selectedFileName) {
      return;
    }
    this.subscriptions.push(this.confirmDiscardChanges().subscribe(ok => ok && this.openFile(file)));
  }

  onNewFile(): void {
    this.subscriptions.push(this.confirmDiscardChanges().subscribe(ok => ok && this.startNewFile()));
  }

  onDiscard(): void {
    const file = this.selectedFile;
    if (file) {
      this.openFile(file);
    } else {
      this.startNewFile();
    }
  }

  onSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    const fileName = this.formControlFileName.getRawValue();
    this.subscriptions.push(
      this.dynabotService.saveDynabotKnowledgeFileFn({ fileName, content: this.formControlContent.value }).pipe(
        this.toastService.spinnerObservable(),
        this.toastService.publishErrorNotificationObservable({
          errorTitle: 'Unable to save',
          errorMessage: `${fileName} could not be saved. Please try again.`,
          retryFn: () => this.onSave(),
        }),
        tap(response => this.toastService.success(response.message, 'DynaBot context saved')),
      ).subscribe(response => {
        this.files = this.upsertFile(response.payload);
        this.openFile(response.payload);
      })
    );
  }

  private selectFirstFile(): void {
    if (this.files.length) {
      this.openFile(this.files[0]);
    } else {
      this.startNewFile();
    }
  }

  private openFile(file: DynabotKnowledgeFile): void {
    this.selectedFileName = file.fileName;
    this.formGroup.reset({ fileName: file.fileName, content: file.content });
    this.formControlFileName.disable();
  }

  private startNewFile(): void {
    this.selectedFileName = null;
    this.formControlFileName.enable();
    this.formGroup.reset({ fileName: '', content: '' });
  }

  private upsertFile(saved: DynabotKnowledgeFile): DynabotKnowledgeFile[] {
    return this.files.some(f => f.fileName === saved.fileName)
      ? this.files.map(f => f.fileName === saved.fileName ? saved : f)
      : [...this.files, saved];
  }

  private confirmDiscardChanges(): Observable<boolean> {
    if (!this.formGroup.dirty) {
      return of(true);
    }
    return this.dialogService.openConfirmationDialog({
      message: 'Discard unsaved changes?',
      subMessage: 'Your edits to this file have not been saved.',
    }).afterClosed().pipe(map((r: ConfirmationDialogResult | undefined) => !!r?.readyForSubmission));
  }

  private fileNameTakenValidator(control: AbstractControl): ValidationErrors | null {
    return this.isNewFile && this.files?.some(f => f.fileName === control.value) ? { taken: true } : null;
  }
}
